import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { GetTasksInput, GetTasksQuery, Task } from "../tasks.types";
import { useGetTasks } from "../tasks.query";
import { TaskStatuses } from "../TaskStatuses";
import { TaskPriorities } from "../TaskPriorities";
import { TaskView } from "../enums";

type GroupBy = "status" | "priority" | "time_category" | "due_date";
type DueDateGroupKey =
  | "overdue"
  | "today"
  | "tomorrow"
  | "this_week"
  | "next_week"
  | "later"
  | "no_due_date";

const DueDateGroups: Array<{ key: DueDateGroupKey; label: string }> = [
  { key: "overdue", label: "Overdue" },
  { key: "today", label: "Today" },
  { key: "tomorrow", label: "Tomorrow" },
  { key: "this_week", label: "This Week" },
  { key: "next_week", label: "Next Week" },
  { key: "later", label: "Later" },
  { key: "no_due_date", label: "No Due Date" },
];

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const addDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const startOfWeek = (date: Date, weekStartsOn = 1) => {
  const day = date.getDay();
  const delta = (day - weekStartsOn + 7) % 7;
  return addDays(startOfDay(date), -delta);
};

const getDueDateGroup = (dueDate: string | null, referenceDate: Date) => {
  if (!dueDate) {
    return "no_due_date";
  }

  const parsedDueDate = new Date(dueDate);
  if (Number.isNaN(parsedDueDate.getTime())) {
    return "no_due_date";
  }

  const dueDateStart = startOfDay(parsedDueDate);
  const todayStart = startOfDay(referenceDate);
  const tomorrowStart = addDays(todayStart, 1);
  const dayAfterTomorrowStart = addDays(todayStart, 2);
  const nextWeekStart = addDays(startOfWeek(referenceDate), 7);
  const weekAfterNextStart = addDays(nextWeekStart, 7);

  if (dueDateStart < todayStart) {
    return "overdue";
  }

  if (dueDateStart < tomorrowStart) {
    return "today";
  }

  if (dueDateStart < dayAfterTomorrowStart) {
    return "tomorrow";
  }

  if (dueDateStart < nextWeekStart) {
    return "this_week";
  }

  if (dueDateStart < weekAfterNextStart) {
    return "next_week";
  }

  return "later";
};

export type TaskSystemFilter = {
  search: string;
};
export type TaskSystemContextType = {
  tasks: Task[];
  setTasks: React.Dispatch<React.SetStateAction<Task[]>>;
  taskView: TaskView;
  setTaskView: React.Dispatch<React.SetStateAction<TaskView>>;
  setQuery: React.Dispatch<React.SetStateAction<GetTasksQuery>>;
  filter: TaskSystemFilter;
  setFilter: React.Dispatch<React.SetStateAction<TaskSystemFilter>>;
  addTask: (task: Task) => void;
  updateTask: (uuid: string, data: Partial<Task>) => void;
  deleteTask: (task: Task) => void;
  setGroupBy: React.Dispatch<React.SetStateAction<GroupBy>>;
  groupBy: GroupBy;
  groupLabels: Record<string, string>;
  groupOrder: string[];
  getCardGroup: (card: Task) => string;
};

const TaskSystemContext = createContext<TaskSystemContextType | undefined>(
  undefined
);

export interface TaskSystemContextProviderProps {
  children: React.ReactNode;
  query?: GetTasksQuery;
}

export const TaskSystemContextProvider = ({
  children,
  query: initialQuery,
}: TaskSystemContextProviderProps) => {
  const [query, setQuery] = useState<GetTasksQuery>(initialQuery || {});
  const [filter, setFilter] = useState<TaskSystemFilter>({ search: "" });

  const { data, isLoading } = useGetTasks(query || {});

  const [tasks, setTasks] = useState<Task[]>(data?.data || []);
  const [taskView, setTaskView] = useState<TaskView>(TaskView.KANBAN);

  const [groupBy, setGroupBy] = useState<GroupBy>("status");

  // TODO: consider pulling from API response instead of using local state
  const timeCategories = useMemo(() => {
    return tasks
      .map((task) => task.time_category)
      .filter((timeCategory) => timeCategory !== null)
      .concat([{ id: 0, name: "No Category" }])
      .sort((a, b) => a.id - b.id);
  }, [tasks]);

  const addTask = (task: Task) => {};

  const updateTask = (uuid: string, data: Partial<Task>) => {
    const updatedTask = tasks.find((task) => task.uuid === uuid);
    if (!updatedTask) {
      return;
    }

    if (data.status !== undefined) {
      updatedTask.status = data.status;
    }
    if (data.priority !== undefined) {
      updatedTask.priority = data.priority;
    }
    if (data.time_category !== undefined) {
      updatedTask.time_category = data.time_category;
    }
    if (data.sort_value !== undefined) {
      updatedTask.sort_value = data.sort_value;
    }

    setTasks((previous) => {
      return previous.map((task) => (task.uuid === uuid ? updatedTask : task));
    });
  };

  const deleteTask = (task: Task) => {};

  const resolveGroupBy = useCallback(
    (groupBy: GroupBy) => {
      let groupLabels: Record<string, string> = {};
      let groupOrder: string[] = [];
      let getCardGroup: (card: Task) => string = () => "";

      switch (groupBy) {
        case "status":
          groupLabels = TaskStatuses.reduce<Record<string, string>>(
            (acc, status) => {
              acc[status.status_id.toString()] = status.status_name;
              return acc;
            },
            {}
          );
          groupOrder = TaskStatuses.map((status) =>
            status.status_id.toString()
          );
          getCardGroup = (card: Task) => card.status?.id?.toString() || "";
          break;
        case "priority":
          groupLabels = TaskPriorities.reduce<Record<string, string>>(
            (acc, priority) => {
              acc[priority.priority_id.toString()] = priority.priority_name;
              return acc;
            },
            {}
          );
          groupOrder = TaskPriorities.map((priority) =>
            priority.priority_id.toString()
          );
          getCardGroup = (card: Task) => card.priority?.id?.toString() || "";
          break;
        case "time_category":
          groupLabels = timeCategories?.reduce<Record<string, string>>(
            (acc, timeCategory) => {
              acc[timeCategory.id.toString()] = timeCategory.name;
              return acc;
            },
            {}
          );
          groupOrder =
            timeCategories?.map((timeCategory) => timeCategory.id.toString()) ||
            [];
          getCardGroup = (card: Task) =>
            card.time_category?.id?.toString() || "0";
          break;
        case "due_date": {
          const referenceDate = new Date();
          groupLabels = DueDateGroups.reduce<Record<string, string>>(
            (acc, dueDateGroup) => {
              acc[dueDateGroup.key] = dueDateGroup.label;
              return acc;
            },
            {}
          );
          groupOrder = DueDateGroups.map((dueDateGroup) => dueDateGroup.key);
          getCardGroup = (card: Task) =>
            getDueDateGroup(card.due_date || null, referenceDate);
          break;
        }
        // Default to status
        default:
          groupLabels = TaskStatuses.reduce<Record<string, string>>(
            (acc, status) => {
              acc[status.status_id.toString()] = status.status_name;
              return acc;
            },
            {}
          );
          groupOrder = TaskStatuses.map((status) =>
            status.status_id.toString()
          );
          getCardGroup = (card: Task) => card.status?.id?.toString() || "";
          break;
      }

      return {
        groupLabels,
        groupOrder,
        getCardGroup,
      };
    },
    [timeCategories]
  );

  const getFilteredTasks = (tasks: Task[]) => {
    const parseSearch = filter.search.toLowerCase().trim();
    return tasks.filter((task) => {
      const nameMatch =
        task?.task_name?.toLowerCase()?.trim()?.includes(parseSearch) ?? false;
      const descriptionMatch =
        task?.description?.toLowerCase()?.trim()?.includes(parseSearch) ??
        false;
      return nameMatch || descriptionMatch;
    });
  };

  const { groupLabels, groupOrder, getCardGroup } = resolveGroupBy(groupBy);
  const filteredTasks = getFilteredTasks(tasks);

  /**
   * Set the tasks when the data is loaded - we need to use local state for tasks because
   * the drag and drop has added latency when managing the sort_value via the query client.
   */
  useEffect(() => {
    if (data && !isLoading) {
      setTasks(data.data);
    }
  }, [data, isLoading]);

  return (
    <TaskSystemContext.Provider
      value={{
        tasks: filteredTasks,
        setTasks,
        taskView,
        setTaskView,
        setQuery,
        filter,
        setFilter,
        addTask,
        updateTask,
        deleteTask,
        setGroupBy,
        groupBy,
        groupLabels,
        groupOrder,
        getCardGroup,
      }}
    >
      {children}
    </TaskSystemContext.Provider>
  );
};

export const useTaskSystemContext = () => {
  const context = useContext(TaskSystemContext);
  if (!context) {
    throw new Error(
      "useTaskSystemContext must be used within a TaskSystemContextProvider"
    );
  }
  return context;
};
